import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Icone } from '../../design/Icone';
import {
  diaCurto,
  emReais,
  haQuanto,
  lerDistribuidoras,
  lerHistorico,
  lerNotas,
  minutosDesde,
  textoDaFalha,
} from '../../encomendas';
import type { CompraDoHistorico, MarcadorDaNota, OfertaDaDistribuidora } from '../../encomendas';
import { usarBloco } from './usarBloco';
import type { EstadoDoBloco } from './usarBloco';

/**
 * As três colunas da consulta. Cada uma: título, fonte, "atualizado há", carregando, erro e
 * "tentar de novo" PRÓPRIOS — uma integração fora não derruba as outras (pedido do dono, item 38).
 * 🔒 Custo e margem: o servidor manda `null` para quem não tem `loja.ver`; aqui a linha some.
 */

function Coluna<T>({
  id,
  titulo,
  fonte,
  estado,
  aoTentar,
  acoes,
  children,
}: {
  id: string;
  titulo: string;
  fonte: string;
  estado: EstadoDoBloco<T>;
  aoTentar: () => void;
  acoes?: ReactNode;
  children: (dados: T) => ReactNode;
}) {
  return (
    <section className={`enc-col enc-col-${id}`} aria-labelledby={`enc-col-${id}`}>
      <header className="enc-col-cabeca">
        <div>
          <h2 id={`enc-col-${id}`}>{titulo}</h2>
          <span className="enc-col-fonte">
            Fonte: {fonte}
            {estado.fase === 'pronto' && <> · lido {haQuanto(minutosDesde(estado.lidoEm))}</>}
          </span>
        </div>
        {acoes}
      </header>
      <div className="enc-col-corpo">
        {estado.fase === 'carregando' && <ListaEsqueleto />}
        {estado.fase === 'falha' && (
          <div className="enc-col-falha" role="alert">
            <Icone nome="warn" tamanho={18} traco={1.7} />
            <p>{estado.mensagem}</p>
            <button type="button" className="enc-botao-sec" onClick={aoTentar}>
              <Icone nome="retry" tamanho={14} traco={1.8} /> Tentar de novo
            </button>
          </div>
        )}
        {estado.fase === 'pronto' && children(estado.dados)}
      </div>
    </section>
  );
}

function ListaEsqueleto() {
  return (
    <div className="enc-lista-esq" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="enc-esqueleto enc-linha-esq">
          <i />
          <i />
        </div>
      ))}
    </div>
  );
}

function Vazio({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="enc-col-vazio">
      <b>{titulo}</b>
      <span>{texto}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Histórico de compras (InovaPED)
 * ------------------------------------------------------------------ */

export function ColunaHistorico({ produtoId, veCusto }: { produtoId: string; veCusto: boolean }) {
  const { estado, recarregar } = usarBloco(
    () => lerHistorico(produtoId),
    (r) => r.historico,
    textoDaFalha,
    produtoId,
  );
  return (
    <Coluna
      id="historico"
      titulo="Histórico de compras"
      fonte="InovaPED"
      estado={estado}
      aoTentar={() => recarregar()}
    >
      {(h) =>
        h.compras.length === 0 ? (
          <Vazio
            titulo="Nenhuma compra registrada"
            texto="O InovaPED não tem pedido nem entrada deste produto."
          />
        ) : (
          <>
            {h.ultima && <UltimaCompra c={h.ultima} veCusto={veCusto} />}
            {veCusto && (
              <dl className="enc-indicadores">
                <div>
                  <dt>Menor custo 90 dias</dt>
                  <dd>{emReais(h.indicadores.menorCusto)}</dd>
                </div>
                <div>
                  <dt>Custo médio</dt>
                  <dd>{emReais(h.indicadores.custoMedio)}</dd>
                </div>
                <div>
                  <dt>Maior custo</dt>
                  <dd>{emReais(h.indicadores.maiorCusto)}</dd>
                </div>
                <div>
                  <dt>Qtd. comprada</dt>
                  <dd>{String(h.indicadores.quantidadeTotal)}</dd>
                </div>
              </dl>
            )}
            <ul className="enc-linhas">
              {h.compras.map((c, i) => (
                <li key={`${c.tipo}-${String(i)}`} className="enc-linha">
                  <div className="enc-linha-topo">
                    <b>
                      {c.fornecedor ??
                        (c.tipo === 'PEDIDO' ? 'Pedido' : 'Fornecedor não informado')}
                    </b>
                    <span className="enc-linha-data">{diaCurto(c.data)}</span>
                  </div>
                  <div className="enc-linha-detalhe">
                    <span className={c.tipo === 'PEDIDO' ? 'enc-tag enc-tag-info' : 'enc-tag'}>
                      {c.tipo === 'PEDIDO' ? `Pedido${c.pedido ? ` #${c.pedido}` : ''}` : 'Entrada'}
                    </span>
                    {c.quantidade !== null && <span>{String(c.quantidade)} un</span>}
                    {veCusto && c.custoUnitario && (
                      <span className="enc-mono">{emReais(c.custoUnitario)} un</span>
                    )}
                    {c.comprador && <span>por {c.comprador}</span>}
                    {c.status && c.tipo === 'PEDIDO' && <span>{c.status}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )
      }
    </Coluna>
  );
}

function UltimaCompra({ c, veCusto }: { c: CompraDoHistorico; veCusto: boolean }) {
  return (
    <div className="enc-destaque">
      <span className="enc-destaque-rotulo">Última compra</span>
      <b className="enc-destaque-data">{diaCurto(c.data)}</b>
      <dl>
        <div>
          <dt>Fornecedor</dt>
          <dd>{c.fornecedor ?? '—'}</dd>
        </div>
        <div>
          <dt>Quantidade</dt>
          <dd>{c.quantidade === null ? '—' : String(c.quantidade)}</dd>
        </div>
        {veCusto && (
          <div>
            <dt>Custo</dt>
            <dd className="enc-mono">{emReais(c.custoUnitario)}</dd>
          </div>
        )}
        {c.pedido && (
          <div>
            <dt>Pedido</dt>
            <dd className="enc-mono">#{c.pedido}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Notas fiscais (HOS)
 * ------------------------------------------------------------------ */

const MARCADOR: Readonly<Record<MarcadorDaNota, { rotulo: string; classe: string }>> = {
  NF_EMITIDA: { rotulo: 'Faturada · a caminho', classe: 'enc-tag-problema' },
  EM_PROCESSAMENTO: { rotulo: 'Em processamento', classe: 'enc-tag-atencao' },
  ENTROU_NO_HOS: { rotulo: 'Entrou no HOS', classe: 'enc-tag-ok' },
  CONFERIDA: { rotulo: 'Conferida', classe: 'enc-tag-ok' },
};

export function ColunaNotas({ produtoId, veCusto }: { produtoId: string; veCusto: boolean }) {
  const { estado, recarregar } = usarBloco(
    () => lerNotas(produtoId),
    (r) => r.notas,
    textoDaFalha,
    produtoId,
  );
  return (
    <Coluna
      id="notas"
      titulo="Notas fiscais"
      fonte="HOS"
      estado={estado}
      aoTentar={() => recarregar()}
    >
      {(d) => (
        <>
          {d.hosIndisponivel && (
            <p className="enc-faixa-aviso" role="status">
              <Icone nome="warn" tamanho={15} traco={1.8} /> Notas do HOS indisponíveis agora —{' '}
              {d.hosIndisponivel.mensagem} Abaixo, só as que o InovaPED conhece.
            </p>
          )}
          {d.notas.length === 0 ? (
            <Vazio
              titulo="Nenhuma nota com este produto"
              texto="Nenhuma NF recente traz este item."
            />
          ) : (
            <ul className="enc-linhas">
              {d.notas.map((n, i) => {
                const m = MARCADOR[n.marcador];
                return (
                  <li key={`${n.chave ?? n.numero ?? ''}-${String(i)}`} className="enc-linha">
                    <div className="enc-linha-topo">
                      <b>{n.fornecedor ?? 'Fornecedor não informado'}</b>
                      <span className="enc-linha-data">{diaCurto(n.data)}</span>
                    </div>
                    <div className="enc-linha-detalhe">
                      <span className={`enc-tag ${m.classe}`}>{m.rotulo}</span>
                      {n.numero && <span className="enc-mono">NF {n.numero}</span>}
                      {n.quantidade !== null && <span>{String(n.quantidade)} un</span>}
                      {veCusto && n.custoUnitario && (
                        <span className="enc-mono">{emReais(n.custoUnitario)} un</span>
                      )}
                      {n.bonificacao && <span className="enc-tag">Bonificação</span>}
                      {n.pedido && <span>Pedido #{n.pedido}</span>}
                    </div>
                    {n.cnpj && <div className="enc-linha-cnpj">CNPJ {formatarCnpj(n.cnpj)}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Coluna>
  );
}

function formatarCnpj(c: string): string {
  return c.length === 14
    ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`
    : c;
}

/* ------------------------------------------------------------------ *
 * Disponibilidade agora (InovaPED)
 * ------------------------------------------------------------------ */

type Filtro = 'todos' | 'disponiveis' | 'menor_custo' | 'maior_margem' | 'menor_prazo';

const ROTULO_DA_SITUACAO: Readonly<Record<OfertaDaDistribuidora['situacao'], string>> = {
  DISPONIVEL: 'Disponível',
  DISPONIVEL_SEM_QUANTIDADE: 'Disponível (sem quantidade)',
  SEM_ESTOQUE: 'Indisponível',
  NAO_INFORMADO: 'Estoque não informado',
};

export function ColunaDistribuidoras({
  produtoId,
  veCusto,
  aoEncomendar,
}: {
  produtoId: string;
  veCusto: boolean;
  aoEncomendar?: (o: OfertaDaDistribuidora) => void;
}) {
  const { estado, recarregar, atualizando } = usarBloco(
    (forcar) => lerDistribuidoras(produtoId, forcar),
    (r) => r.disponibilidade,
    textoDaFalha,
    produtoId,
  );
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [tentativas, setTentativas] = useState(0);

  // O robô do InovaPED ainda consultando: repete sozinho (até ~1 min), sem travar a tela.
  const procurando = estado.fase === 'pronto' && estado.dados.procurando;
  useEffect(() => {
    if (!procurando || tentativas >= 20) return;
    const t = window.setTimeout(() => {
      setTentativas((n) => n + 1);
      recarregar();
    }, 3_000);
    return () => window.clearTimeout(t);
  }, [procurando, tentativas, recarregar]);

  const filtros: readonly [Filtro, string][] = [
    ['todos', 'Todos'],
    ['disponiveis', 'Só disponíveis'],
    ...(veCusto
      ? ([
          ['menor_custo', 'Menor custo'],
          ['maior_margem', 'Maior margem'],
        ] as [Filtro, string][])
      : []),
    ['menor_prazo', 'Menor prazo'],
  ];

  return (
    <Coluna
      id="distribuidoras"
      titulo="Disponibilidade agora"
      fonte="InovaPED"
      estado={estado}
      aoTentar={() => recarregar(true)}
      acoes={
        <button
          type="button"
          className="enc-botao-sec"
          onClick={() => {
            setTentativas(0);
            recarregar(true);
          }}
          disabled={atualizando}
          aria-busy={atualizando}
        >
          <Icone nome="retry" tamanho={14} traco={1.8} />{' '}
          {atualizando ? 'Atualizando…' : 'Atualizar consulta'}
        </button>
      }
    >
      {(d) => (
        <>
          <div className="enc-filtros" role="group" aria-label="Ordenar e filtrar">
            {filtros.map(([k, rotulo]) => (
              <button
                key={k}
                type="button"
                className={filtro === k ? 'enc-filtro-sel' : ''}
                aria-pressed={filtro === k}
                onClick={() => setFiltro(k)}
              >
                {rotulo}
              </button>
            ))}
          </div>
          {d.procurando ? (
            <div className="enc-procurando" role="status">
              <span className="enc-girando" aria-hidden="true" />
              Consultando as distribuidoras…
            </div>
          ) : (
            <ListaDeOfertas
              ofertas={d.ofertas}
              filtro={filtro}
              veCusto={veCusto}
              {...(aoEncomendar ? { aoEncomendar } : {})}
            />
          )}
        </>
      )}
    </Coluna>
  );
}

function ListaDeOfertas({
  ofertas,
  filtro,
  veCusto,
  aoEncomendar,
}: {
  ofertas: readonly OfertaDaDistribuidora[];
  filtro: Filtro;
  veCusto: boolean;
  aoEncomendar?: (o: OfertaDaDistribuidora) => void;
}) {
  const lista = useMemo(() => {
    const n = (v: string | null) => (v === null ? null : Number(v));
    const disponiveis = ofertas.filter((o) => o.disponivel);
    const resto = ofertas.filter((o) => !o.disponivel);
    const ordenar = (
      xs: OfertaDaDistribuidora[],
      chave: (o: OfertaDaDistribuidora) => number | null,
      desc = false,
    ) =>
      [...xs].sort((a, b) => {
        const va = chave(a);
        const vb = chave(b);
        if (va === null) return 1;
        if (vb === null) return -1;
        return desc ? vb - va : va - vb;
      });
    switch (filtro) {
      case 'disponiveis':
        return disponiveis;
      case 'menor_custo':
        return [...ordenar(disponiveis, (o) => n(o.custo)), ...resto];
      case 'maior_margem':
        return [...ordenar(disponiveis, (o) => n(o.margemEstimada), true), ...resto];
      case 'menor_prazo':
        return [...ordenar(disponiveis, (o) => o.prazoDias), ...resto];
      default:
        return [...ofertas];
    }
  }, [ofertas, filtro]);

  if (lista.length === 0)
    return (
      <Vazio
        titulo={
          filtro === 'disponiveis'
            ? 'Nenhuma distribuidora tem agora'
            : 'Nenhuma distribuidora encontrou o produto'
        }
        texto="Tente “Atualizar consulta” em alguns minutos."
      />
    );
  return (
    <ul className="enc-ofertas">
      {lista.map((o) => (
        <li key={o.slug ?? o.distribuidora}>
          <article className={`enc-oferta${o.disponivel ? '' : ' enc-oferta-off'}`}>
            <header className="enc-oferta-cabeca">
              <b className="enc-oferta-nome">{o.distribuidora}</b>
              <span className={`enc-status enc-status-${o.tom}`}>
                <i aria-hidden="true" />
                {ROTULO_DA_SITUACAO[o.situacao]}
                {o.estoque !== null && ` · ${String(o.estoque)} un`}
              </span>
            </header>
            <dl className="enc-oferta-precos">
              {veCusto && (
                <div>
                  <dt>Custo</dt>
                  <dd className="enc-mono">{emReais(o.custo)}</dd>
                </div>
              )}
              <div>
                <dt>Preço de venda (HOS)</dt>
                <dd className="enc-mono">{emReais(o.precoInova)}</dd>
              </div>
              <div>
                <dt>PMC</dt>
                <dd className="enc-mono">{emReais(o.pmc)}</dd>
              </div>
              {veCusto && o.disponivel && (
                <div>
                  <dt>Margem est.</dt>
                  <dd
                    className={`enc-mono ${o.margemEstimada !== null && Number(o.margemEstimada) <= 0 ? 'enc-n-zero' : 'enc-n-ok'}`}
                  >
                    {emReais(o.margemEstimada)}
                  </dd>
                </div>
              )}
            </dl>
            <div className="enc-oferta-extra">
              {o.prazoDias !== null && (
                <span>
                  Prazo {String(o.prazoDias)} {o.prazoDias === 1 ? 'dia' : 'dias'}
                </span>
              )}
              {o.descontoPct !== null && o.descontoPct > 0 && (
                <span>Desconto {String(o.descontoPct)}%</span>
              )}
              {veCusto && o.condicaoComercial && <span>{o.condicaoComercial}</span>}
              <span className={o.velha ? 'enc-velha' : ''}>
                {o.velha ? 'Carga antiga · ' : ''}Atualizado {haQuanto(o.idadeMin)}
              </span>
            </div>
            {o.disponivel && (
              <button
                type="button"
                className="enc-botao enc-encomendar"
                onClick={() => aoEncomendar?.(o)}
                disabled={!aoEncomendar}
                title={aoEncomendar ? undefined : 'O formulário da encomenda chega na fase 3'}
              >
                <Icone nome="encomendar" tamanho={16} traco={1.8} />
                Encomendar
              </button>
            )}
          </article>
        </li>
      ))}
    </ul>
  );
}
